import { useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useRecoilCallback } from 'recoil';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import * as H from 'history';

import * as K from 'common/constants';
import {
  buildUrl,
  getScalyrPath,
  getUrlParam,
  getUrlParamSet,
  getWhitelistedUrlParams,
  isRegisteredRoute,
  isTeamTokenClearingPage,
} from 'common/util/location';
import { applyParamsToSettings, getDisplaySettingsFromParams } from 'common/util/settings';
import { UrlParams } from 'common/types';
import {
  activeRequestTimeState,
  draftSearchParamByRouteState,
  eventParamRecordState,
  locationState,
  originState,
  ROUTE_FROM_SERVER,
  routeState,
  searchParamState,
  selectedTokenState,
  teamTokenState,
} from 'components/common/Managers/RouteStateManager.state';
import { CustomLocationState, scrollToHash, updateUrl } from 'components/common/Managers/RouteStateManager.utils';
import { settingsState } from 'components/common/Managers/SettingsManager.state';

/**
 * Routing state management component. This component never renders content.
 *
 * @prop history        mutable browser history object from the history library (supplied by react-router)
 */

function RouteStateManager({ history }: RouteComponentProps<{}, {}, CustomLocationState>): JSX.Element {
  const updateURLState = useRecoilCallback(({ snapshot, set }) => ({ location }: { location: H.Location }) => {
    /**
     * If the incoming url does not match a registered route, then redirect to the server, with the following caveat:
     * If the incoming route is the first route "seen" by the app, then it is presumed that this route was deliberately
     * served in the react app.
     *
     * In this scenario, the react app disregards both the whitelist and the blacklist and processes the incoming url in
     * react regardless of whether it is registered from the client's point of view. This helps prevent infinite
     * redirect loops, and it establishes the server as the final authority on how routes should be handled.
     *
     * This side effect may be a candidate for future improvements. Its future may depend on the future of react-router,
     * SERVER_DATA, retiring the legacy app, and the whitelist and blacklist.
     */

    const settings = snapshot.getLoadable(settingsState).getValue();
    const { reactPaths, reactPathsBlacklist } = settings;

    const isIncomingRouteRegistered = isRegisteredRoute(location, reactPaths, reactPathsBlacklist);
    const isIncomingRouteFromServer = getScalyrPath(location) === ROUTE_FROM_SERVER;
    if (!isIncomingRouteRegistered && !isIncomingRouteFromServer) {
      // redirect to server
      window.location.href = buildUrl(location.pathname, location.search);
    }

    // https://github.com/remix-run/history/issues/930#issuecomment-1035203553
    const state = location.state as CustomLocationState;

    const { eventParamList, viewParamList, disposableParamList } = getUrlParamSet(location);
    const eventParams = getWhitelistedUrlParams(location.search, eventParamList);
    const prevEventParams = snapshot.getLoadable(eventParamRecordState).valueMaybe() || {};
    const eventParamsChanged = !isEqual(eventParams, prevEventParams) || state?.forceUpdate || false;

    const previousTeamToken = snapshot.getLoadable(teamTokenState).valueMaybe() || '';
    const urlTeamToken = getUrlParam(location.search, K.URL_PARAM.TEAM_TOKEN);

    // First we need to handle possible additional history updates before doing any side effects of updating state.
    // Here we keep track of whether a history update is needed, and what parameters/location.state need updating.
    let doHistoryReplace = false;
    let updatedParams = {} as UrlParams;
    const disposableParamState = {} as UrlParams;

    // If teamToken is not present in the url, and the page allows is, use the teamToken we already have in state.
    if (!urlTeamToken && previousTeamToken && !isTeamTokenClearingPage(location.pathname)) {
      doHistoryReplace = true;
      updatedParams[K.URL_PARAM.TEAM_TOKEN] = previousTeamToken;
    }

    if (disposableParamList.length) {
      disposableParamList.forEach((paramName) => {
        const paramValue = getUrlParam(location.search, paramName);
        if (paramValue) {
          disposableParamState[paramName] = paramValue;
        }
      });

      if (Object.values(disposableParamState).length) {
        doHistoryReplace = true;
        const clearedParams = disposableParamList.reduce(
          (acc, param): UrlParams => ({ [param]: '', ...acc }),
          {} as UrlParams,
        );
        updatedParams = { ...updatedParams, ...clearedParams };
      }
    }

    // If we need to replace the current history entry with some updates, do so here, and exit early so we avoid processing any side effects.
    // updateURLState will run again after the history replace, and the side effects will be processed at that time.
    if (doHistoryReplace) {
      const updated = updateUrl(updatedParams, true, history, {
        ...state,
        disposableParams: { ...disposableParamState },
      });
      if (updated) {
        return;
      }
    }

    scrollToHash(history);

    const setTeamToken = (teamToken: string): void => {
      set(teamTokenState, teamToken);
    };

    // If the teamToken parameter is present in the url, then we treat the url
    // as the source of truth and update the teamToken in state.
    if (urlTeamToken) {
      setTeamToken(urlTeamToken);
    }

    // When navigating to special pages, we must clean team token to prevent its further
    // collision with a new one.
    if (isTeamTokenClearingPage(location.pathname)) {
      setTeamToken('');
    }

    const currentHistoryState = state;
    if (
      currentHistoryState?.disposableParams?.[K.URL_PARAM.CENTERED] &&
      currentHistoryState?.disposableParams?.[K.URL_PARAM.CONTINUATION_TOKEN]
    ) {
      set(selectedTokenState, currentHistoryState?.disposableParams?.[K.URL_PARAM.CONTINUATION_TOKEN]);
    }

    // update settings with any new URL param values, if any were passed
    const settingsParams = getDisplaySettingsFromParams(currentHistoryState?.disposableParams || ({} as UrlParams));
    if (Object.keys(settingsParams).length > 0) {
      set(settingsState, (curSettings) => applyParamsToSettings(curSettings, settingsParams));
    }

    // if eventParams change, we set a new Moment.moment to {@link activeRequestTimeState}.
    if (eventParamsChanged) {
      set(activeRequestTimeState, moment());
    }

    set(locationState, location);
    // TODO: Is this the best way to do this? {@link https://github.com/ReactTraining/history} does not track `origin`
    set(originState, window?.location?.origin || '');
    const newRoute = getScalyrPath(location);
    set(routeState, newRoute);

    // if the route is changing, cleanup the state for the previous route
    const route = snapshot.getLoadable(routeState).getValue();
    if (newRoute !== route) {
      set(draftSearchParamByRouteState(route), {} as UrlParams);
    }
    // sync draft params to the location in the same way the {@link searchParamRecordState} selector derives its state
    set(
      draftSearchParamByRouteState(newRoute),
      getWhitelistedUrlParams(location.search, [...eventParamList, ...viewParamList]),
    );

    // TODO: This code could possibly be abstracted, but it also might need to change.
    // It isn't clear if we want to also include disposable params which now live on the locationState object instead of in the URL.
    [...eventParamList, ...viewParamList].forEach((paramName) => {
      const paramValue = getUrlParam(location.search, paramName);
      set(searchParamState(paramName), paramValue);
    });

    const newEventParams = {} as UrlParams;
    [...eventParamList].forEach((paramName) => {
      const paramValue = getUrlParam(location.search, paramName);
      newEventParams[paramName] = paramValue;
    });
    set(eventParamRecordState, newEventParams);
  });

  useEffect(() => {
    const unlisten = history.listen(updateURLState);
    // stop the listener when component unmounts
    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure that updateURLState runs on initial load of the app.
  useEffect(() => {
    // If the team token is present in the URL on our first render, we run updateURLState,
    // If the team token is not present in the URL, we skip this call as apollo middleware will
    // trigger a history.replace to update the URL with the team token and history.listen will run.
    if (getUrlParam(history.location.search, K.URL_PARAM.TEAM_TOKEN)) {
      updateURLState({ location: history.location });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
}

export default withRouter(RouteStateManager);
